import fs from 'node:fs'
import { dirname } from 'node:path'
import { Command } from 'commander'
import z from 'zod'

import { publishRecordSchema } from '@scalar-org/entities/project'
import { slugSchema } from '@scalar-org/entities/utility'
import { parseConfig } from '@scalar-org/scalar-config'

import { findConfig } from '@/domains/project/helpers'
import {
  publishGithub,
  publishProject,
} from '@/domains/project/publish/publish'
import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'
import { sleep } from '@/helpers/utils'

export const PublishCommand = () => {
  const cmd = new Command('publish')

  cmd.description(
    'Publish new build for a github sync project that is not linked.',
  )
  cmd.option('-s, --slug [slug]', 'Project slug found in Scalar Dashboard')
  cmd.option('-c, --config [config]', 'Your config file of the project')
  cmd.option('-p, --preview', 'Publish in preview mode')
  cmd.option(
    '-g, --github',
    'Publish from your linked remote GitHub repository',
  )

  cmd.action(async (args) => {
    const argsParseResult = z
      .object({
        slug: slugSchema,
        config: z.string().optional(),
        preview: z.coerce.boolean().default(false),
        github: z.coerce.boolean().default(false),
      })
      .safeParse(args)

    if (argsParseResult.error)
      return output
        .error()
        .title('Invalid Arguments')
        .message(argsParseResult.error.issues[0].message)
        .exit('error')

    const {
      config: configPathOption,
      slug,
      preview,
      github,
    } = argsParseResult.data

    const apiClient = await apiService()
    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    if (!slug) {
      return output
        .error()
        .title('Invalid argument')
        .message('Slug is required. Try passing --slug flag.')
        .exit('error')
    }

    const gitProject = await getGithubProject(slug, apiClient)

    if (github) {
      if (!gitProject.repository) {
        return output
          .error()
          .title('Your project is not linked to a GitHub repository')
          .message(
            'For a sync publish, please configure your GitHub connection through the dashboard.',
          )
          .exit('error')
      }

      if (gitProject.repository.expired) {
        return output
          .error()
          .title('Your GitHub repository access is expired.')
          .message(
            'Please reconnect your project through the Scalar Dashboard to publish via sync.',
          )
          .exit('error')
      }

      const publishResult = await publishGithub({ apiClient, slug, preview })

      if (!publishResult.success) {
        return output
          .error()
          .title('Could not publish sync project.')
          .message(publishResult.error)
          .exit('error')
      }

      return await waitForPublish({
        teamUid: auth.teamUid,
        publishUid: publishResult.publishUid,
      })
    }

    const configPath = configPathOption ?? findConfig()

    if (!configPath || !fs.existsSync(configPath)) {
      return output
        .error()
        .title('Invalid configuration file')
        .message('Could not find the Scalar Configuration file:')
        .message(configPath ?? '')
        .exit('error')
    }

    const rawConfig = fs.readFileSync(configPath, 'utf-8')
    const configParseResult = parseConfig(rawConfig)

    if (!configParseResult.success) {
      return output
        .error()
        .title('Invalid configuration file')
        .line(
          'Please check your scalar configuration file to match the specification.',
        )
        .message(configPath)
        .exit('error')
    }

    const publishResult = await publishProject({
      config: configParseResult.config,
      gitProject,
      configDir: dirname(configPath),
      configPath,
      preview,
    })

    if (!publishResult.success) {
      return output
        .error()
        .title('Something went wrong while deploying the project')
        .message(
          publishResult.error instanceof Error
            ? publishResult.error.message
            : publishResult.error,
        )
        .exit('error')
    }

    return await waitForPublish({
      teamUid: auth.teamUid,
      publishUid: publishResult.publishUid,
    })
  })

  return cmd
}

async function waitForPublish({
  teamUid,
  publishUid,
}: {
  teamUid: string
  publishUid: string
}) {
  const dbClient = await getDatabaseClient()

  // Start the async flow
  const logger = output.info({ animate: true }).loader('Deployment in progress')

  // Check and update the deployment status since it is an asynchronous flow
  while (true) {
    const deployStatus = await getDeployStatus({
      client: dbClient,
      publishUid,
      teamUid,
    })

    if (deployStatus.error === true) {
      return output
        .error()
        .title(
          'Something went wrong while getting information about the deployment',
        )
        .exit('error')
    }

    logger.update('loader', 0, { content: deployStatus.data.message })

    if (deployStatus.data.status === 'deployed') {
      return logger.exit('success')
    }

    if (deployStatus.data.status === 'error') {
      logger.exit('error')
    }

    // check the status every ~3 second
    await sleep(3000)
  }
}

/**
 * Fetch github project metadata
 */
async function getGithubProject(
  slug: string,
  apiClient: Awaited<ReturnType<typeof apiService>>,
) {
  const project = await apiClient.github.getProject(slug)

  if (project.error) {
    return output
      .error()
      .title('Error fetching github project')
      .message(project.message)
      .exit('error')
  }

  return project.data
}

/** Returns the deploy entry on the database with all information on the current deployment */
async function getDeployStatus({
  client,
  publishUid,
  teamUid,
}: {
  client: Awaited<ReturnType<typeof getDatabaseClient>>
  publishUid: string
  teamUid: string
}) {
  return client.getOne({
    collectionPath: `team/${teamUid}/publish`,
    schema: publishRecordSchema,
    query: { uid: publishUid },
  })
}
